import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AGENT_TEMPLATES } from './agentTemplates';

const ACTION_LABEL = {
  Reply: 'Replies',
  'No Reply': 'Stays silent',
  'Only Tag': 'Tags only',
};

const HOW_IT_WORKS = [
  {
    icon: 'fa-inbox',
    title: 'A reply lands',
    text: '“What would something like this cost us?”',
  },
  {
    icon: 'fa-bullseye',
    title: 'It finds the case',
    text: 'Pricing question. The one that fits best wins.',
  },
  {
    icon: 'fa-pen-nib',
    title: 'The answer waits',
    text: 'You read it, adjust a word, send.',
  },
];

/**
 * AgentTemplatePicker — the gallery of ready-made cases.
 *
 * Inline it is the empty state, and there the gallery starts collapsed. Nine
 * cards of somebody else's cases is a browsing task in front of a writing task;
 * the user's own first case is the faster and better answer, and the ready-made
 * ones stay available for whoever wants them.
 *
 * As a modal it goes through a portal, because the settings pane it lives in
 * scrolls and would clip it.
 */
export default function AgentTemplatePicker({ variant = 'inline', onPick, onClose }) {
  const isModal = variant === 'modal';
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    if (!isModal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [isModal, onClose]);

  const gallery = (withBlank) => (
    <div className="ags-gallery">
      {AGENT_TEMPLATES.map((template) => (
        <button
          key={template.id}
          type="button"
          className={`ags-tpl accent-${template.accent}`}
          onClick={() => onPick(template)}
        >
          <span className="ags-tpl-icon"><i className={`fa ${template.icon}`} /></span>
          <span className="ags-tpl-title">{template.title}</span>
          <span className="ags-tpl-blurb">{template.blurb}</span>
          <span className="ags-tpl-example">{template.example}</span>
          <span className="ags-tpl-foot">
            <span className="ags-tpl-badge">{ACTION_LABEL[template.agent.type]}</span>
            {template.agent.useTag && template.agent.tag && (
              <span className="ags-tpl-tag" style={{ backgroundColor: template.agent.tagColor }}>
                {template.agent.tag}
              </span>
            )}
          </span>
        </button>
      ))}

      {withBlank && (
        <button type="button" className="ags-tpl ags-tpl-blank" onClick={() => onPick(null)}>
          <span className="ags-tpl-icon"><i className="fa fa-plus" /></span>
          <span className="ags-tpl-title">Define your own case</span>
          <span className="ags-tpl-blurb">Start from a blank case.</span>
        </button>
      )}
    </div>
  );

  if (!isModal) {
    return (
      <section className="ags-empty">
        <div className="ags-start">
          <span className="ags-start-icon"><i className="fa fa-wand-magic-sparkles" /></span>
          <h4>Answers, written before you open the email</h4>
          <p>
            Describe the replies you get. Every incoming email is matched to the
            case that fits, and the answer is already there when you look.
          </p>

          {/* The mechanic is the selling point and the thing nobody guesses from
              a form, so the empty state shows it once, in order. */}
          <ol className="ags-how">
            {HOW_IT_WORKS.map((stage) => (
              <li key={stage.title} className="ags-how-step">
                <span className="ags-how-icon"><i className={`fa ${stage.icon}`} /></span>
                <span className="ags-how-title">{stage.title}</span>
                <span className="ags-how-text">{stage.text}</span>
              </li>
            ))}
          </ol>

          <button type="button" className="ags-start-btn" onClick={() => onPick(null)}>
            Create your first case
          </button>
          <button
            type="button"
            className={`ags-start-alt ${showTemplates ? 'open' : ''}`}
            onClick={() => setShowTemplates(!showTemplates)}
            aria-expanded={showTemplates}
          >
            <i className="fa fa-chevron-right" />
            Or start from a ready-made case
          </button>
        </div>

        {showTemplates && gallery(false)}
      </section>
    );
  }

  return createPortal(
    <div className="ags-pickerModal" role="dialog" aria-modal="true" aria-label="Choose a template">
      <div className="ags-pickerScrim" onClick={onClose} />
      <div className="ags-pickerPanel">
        <header className="ags-pickerHead">
          <span className="ags-pickerIcon"><i className="fa fa-layer-group" /></span>
          <div className="ags-pickerHeadText">
            <span className="ags-pickerEyebrow">Ready-made</span>
            <h3>Which situation do you want to cover?</h3>
          </div>
          <button type="button" className="ags-pickerClose" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="ags-pickerBody">{gallery(true)}</div>
      </div>
    </div>,
    document.body,
  );
}
